import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Megaphone, Save, Loader2, Users, MousePointer, ShoppingCart, DollarSign, Ban, Trash2, Copy, ExternalLink, Plus, X, Share2, Mail, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoPartnersTab({ eventId, token, event }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState({ partners: 0, active_partners: 0, total_clicks: 0, total_sales: 0, total_commission_cents: 0 });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageInput, setImageInput] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [sentInvites, setSentInvites] = useState([]);
  const [nudging, setNudging] = useState(null);
  const [domainInput, setDomainInput] = useState('');

  const addDomain = () => {
    let d = domainInput.trim().toLowerCase();
    if (!d) return;
    d = d.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    if (!d.includes('.')) { toast.error('Enter a valid domain like nocturnalhalloween.com'); return; }
    setConfig(c => ({ ...c, partner_domains: [...new Set([...(c.partner_domains || []), d])] }));
    setDomainInput('');
  };

  const nudgeInvite = async (email) => {
    setNudging(email);
    try {
      await axios.post(`${API}/admin/promo/${eventId}/invite-emails`,
        { emails: [email], note: "Just checking in — we'd still love to have you on the promo team for this one!" },
        { headers });
      toast.success(`Nudge sent to ${email}`);
      loadSentInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send nudge');
    } finally { setNudging(null); }
  };

  const loadSentInvites = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo/${eventId}/invite-emails`, { headers });
      setSentInvites(res.data.invites || []);
    } catch { /* non-blocking */ }
  };

  const sendInvites = async () => {
    const count = inviteEmails.split(/[\s,;]+/).filter(e => e.includes('@')).length;
    if (!count) { toast.error('Enter at least one email address'); return; }
    setInviteSending(true);
    try {
      const res = await axios.post(`${API}/admin/promo/${eventId}/invite-emails`,
        { emails: inviteEmails, note: inviteNote }, { headers });
      toast.success(`Invite sent to ${res.data.sent} ${res.data.sent === 1 ? 'person' : 'people'}${res.data.failed?.length ? ` — ${res.data.failed.length} failed` : ''}`);
      setInviteEmails(''); setInviteNote('');
      loadSentInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invites');
    } finally { setInviteSending(false); }
  };

  const fetchAll = async () => {
    try {
      const [c, s, p] = await Promise.all([
        axios.get(`${API}/admin/promo/${eventId}/config`, { headers }),
        axios.get(`${API}/admin/promo/${eventId}/stats`, { headers }),
        axios.get(`${API}/admin/promo/${eventId}/partners`, { headers }),
      ]);
      setConfig(c.data); setStats(s.data); setPartners(p.data.partners || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (eventId) fetchAll(); /* eslint-disable-line */ }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/promo/${eventId}/config`, config, { headers });
      toast.success('Saved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const partnerAction = async (userId, action) => {
    try {
      await axios.post(`${API}/admin/promo/${eventId}/partners/${userId}/${action}`, {}, { headers });
      toast.success('Updated'); fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const copyText = (text, label = 'Link') => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  // Public share URLs
  const origin = (typeof window !== 'undefined' ? window.location.origin : '');
  const directoryUrl = `${origin}/promo-partners`;
  const eventPublicUrl = event?.slug && event?.city ? `${origin}/events/${event.city}/${event.slug}` : '';
  const formatCommission = () => {
    if (!config) return '';
    const flat = (config.commission_flat_cents || 0) / 100;
    const pct = config.commission_percent || 0;
    const parts = [];
    if (flat) parts.push(`$${flat.toFixed(2)}/ticket`);
    if (pct) parts.push(`${pct}% of sale`);
    return parts.join(' + ') || 'set commission';
  };
  const inviteCopy = `Want to make money helping me sell tickets for "${event?.name || 'my event'}"? You'll earn ${formatCommission()} on every sale you bring in.\n\n1) Sign up at ${directoryUrl}\n2) Click "Accept Campaign" on the event\n3) Share your unique tracking link\n\nGet your link → ${directoryUrl}`;

  if (loading || !config) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="promo-partners-tab">
      {/* Header & Toggle */}
      <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-indigo-500/5 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-500/20">
              <Megaphone className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2">Promo Team Mode {config.enabled && <Badge className="bg-emerald-600 text-white text-[10px]">LIVE</Badge>}</h3>
              <p className="text-xs text-muted-foreground">Empower a street team to sell tickets for this event in exchange for commission.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!config.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} data-testid="promo-enabled-switch" />
            <Label className="text-sm">{config.enabled ? 'On' : 'Off'}</Label>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <Stat icon={Users} label="Partners" value={`${stats.active_partners}/${stats.partners}`} testid="stat-partners" />
          <Stat icon={MousePointer} label="Clicks" value={stats.total_clicks} testid="stat-clicks" />
          <Stat icon={ShoppingCart} label="Sales" value={stats.total_sales} testid="stat-sales" />
          <Stat icon={DollarSign} label="Paid out" value={usd(stats.total_commission_cents)} testid="stat-payout" />
        </div>
      </div>

      {/* Share panel — only when live */}
      {config.enabled && (
        <Card className="border-indigo-500/30 bg-indigo-500/5" data-testid="promo-share-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-indigo-500" />
              <h4 className="font-semibold text-sm">Share & invite partners</h4>
            </div>
            <p className="text-xs text-muted-foreground">Send these links to your network. Anyone who clicks "Join the Street Team" gets onboarded automatically and can accept this campaign.</p>

            <div className="space-y-2">
              <div>
                <Label className="text-[11px] uppercase font-semibold text-muted-foreground">Public Promo Team Page (lists all open campaigns)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={directoryUrl} readOnly className="text-xs font-mono h-8" data-testid="share-directory-url" />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copyText(directoryUrl, 'Directory link')} data-testid="share-directory-copy"><Copy className="h-3 w-3" /></Button>
                  <a href={directoryUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="h-8"><ExternalLink className="h-3 w-3" /></Button></a>
                </div>
              </div>

              {eventPublicUrl && (
                <div>
                  <Label className="text-[11px] uppercase font-semibold text-muted-foreground">Public Event Page</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={eventPublicUrl} readOnly className="text-xs font-mono h-8" data-testid="share-event-url" />
                    <Button size="sm" variant="outline" className="h-8" onClick={() => copyText(eventPublicUrl, 'Event link')}><Copy className="h-3 w-3" /></Button>
                    <a href={eventPublicUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="h-8"><ExternalLink className="h-3 w-3" /></Button></a>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase font-semibold text-muted-foreground">Pre-written Invite Message</Label>
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => copyText(inviteCopy, 'Invite copy')} data-testid="share-invite-copy"><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                </div>
                <Textarea readOnly value={inviteCopy} className="font-mono text-[11px] min-h-[110px] mt-1" />
                <Button
                  size="sm"
                  type="button"
                  className="text-xs h-7 mt-2 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="share-email-btn"
                  onClick={() => { setInviteOpen(true); loadSentInvites(); }}
                >
                  <Mail className="h-3 w-3 mr-1" /> Send via Email
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bounty / Commission */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm">Commission (the bounty)</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Flat $ per ticket</Label>
              <div className="flex items-center gap-1">
                <span className="text-sm">$</span>
                <Input type="number" min="0" step="0.01" value={(config.commission_flat_cents / 100) || 0}
                  onChange={e => setConfig(c => ({ ...c, commission_flat_cents: Math.round(parseFloat(e.target.value || '0') * 100) }))}
                  data-testid="promo-flat" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Percent of sale (%)</Label>
              <Input type="number" min="0" max="100" step="0.5" value={config.commission_percent || 0}
                onChange={e => setConfig(c => ({ ...c, commission_percent: parseFloat(e.target.value || '0') }))}
                data-testid="promo-percent" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Both fields combine. e.g. <span className="font-mono">$3 + 5%</span> means a $3 flat fee per ticket plus 5% of the order total.</p>
        </CardContent>
      </Card>

      {/* Marketing Kit */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm">Marketing Kit</h4>
          <div className="space-y-1">
            <Label className="text-xs">Event Directive (what partners should communicate)</Label>
            <Textarea value={config.marketing_directive || ''} onChange={e => setConfig(c => ({ ...c, marketing_directive: e.target.value }))} placeholder="e.g. 21+ rooftop event. Emphasize the sunset jazz set + craft cocktails. Don't post in nightclub groups." className="min-h-[80px]" data-testid="promo-directive" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Pre-written Marketing Copy (partners can copy/paste)</Label>
            <Textarea value={config.marketing_copy || ''} onChange={e => setConfig(c => ({ ...c, marketing_copy: e.target.value }))} placeholder={"🎷 Mile High Rooftop Jazz & Cocktails — Saturday 6/21\nLive jazz + craft cocktails on Denver's hottest rooftop.\nGet your ticket: {LINK}"} className="min-h-[100px] font-mono text-xs" data-testid="promo-copy" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Partner websites with your ticket widget</Label>
            <p className="text-[11px] text-muted-foreground">List the sites where this event's widget is embedded (e.g. nocturnalhalloween.com). Promoters get ready-made tracking links for each — sales on those sites credit them automatically.</p>
            <div className="flex gap-2">
              <Input value={domainInput} onChange={e => setDomainInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                placeholder="nocturnalhalloween.com" className="text-xs" data-testid="partner-domain-input" />
              <Button type="button" size="sm" variant="outline" onClick={addDomain} data-testid="partner-domain-add">Add</Button>
            </div>
            {(config.partner_domains || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="partner-domain-chips">
                {config.partner_domains.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 text-[11px] bg-muted border border-border rounded-full px-2.5 py-1">
                    {d}
                    <button type="button" onClick={() => setConfig(c => ({ ...c, partner_domains: c.partner_domains.filter(x => x !== d) }))} className="text-muted-foreground hover:text-red-400" aria-label={`Remove ${d}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Marketing Images (URLs)</Label>
            <div className="flex gap-2">
              <Input value={imageInput} onChange={e => setImageInput(e.target.value)} placeholder="https://…" className="text-xs" />
              <Button size="sm" onClick={() => { if (imageInput) { setConfig(c => ({ ...c, marketing_images: [...(c.marketing_images || []), imageInput] })); setImageInput(''); } }}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            {(config.marketing_images || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                {config.marketing_images.map((u, i) => (
                  <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-border">
                    <img src={u} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setConfig(c => ({ ...c, marketing_images: c.marketing_images.filter((_, j) => j !== i) }))} className="absolute top-1 right-1 bg-black/70 text-white rounded p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="promo-save-btn">
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save Configuration</>}
          </Button>
        </CardContent>
      </Card>

      {/* Partners */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-2">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Active Partners ({partners.length})</h4>
          {partners.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No partners yet. Once you toggle on Promo Team Mode, an email blast goes to all street team members.</p>
          ) : partners.map(p => (
            <div key={p.acceptance_id} className="flex items-center gap-3 p-2.5 border border-border rounded-md flex-wrap" data-testid={`promo-partner-${p.user_id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-medium">{p.user?.first_name} {p.user?.last_name} <span className="text-muted-foreground font-normal">· {p.user?.email}</span></p>
                <p className="text-[11px] text-muted-foreground font-mono">code: {p.tracking_code} · {p.clicks || 0} clicks · {p.sales_count || 0} sales · earned {usd(p.total_commission_cents)}</p>
              </div>
              {p.status === 'banned' ? (
                <Badge variant="outline" className="text-[10px] bg-rose-500/10 border-rose-500/30 text-rose-500">Banned</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 border-emerald-500/30 text-emerald-500">Active</Badge>
              )}
              {p.status === 'active' ? (
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => partnerAction(p.user_id, 'ban')}><Ban className="h-3 w-3 mr-1" /> Ban</Button>
              ) : (
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => partnerAction(p.user_id, 'unban')}>Unban</Button>
              )}
              <Button size="sm" variant="outline" className="text-xs h-7 text-rose-500 border-rose-500/30" onClick={() => partnerAction(p.user_id, 'remove_from_event')}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Invite-by-email dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-lg" data-testid="promo-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Invite partners by email</DialogTitle>
            <DialogDescription>
              Eventism sends each person a branded invite with your commission offer ({formatCommission()}) and a one-tap "Join the Promo Team" button. Replies come straight to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Email addresses <span className="text-muted-foreground">(comma or new line separated, up to 50)</span></Label>
              <Textarea
                value={inviteEmails}
                onChange={e => setInviteEmails(e.target.value)}
                placeholder={"maria@influencer.com\nevents@denvernightlife.com\njake@gmail.com"}
                className="min-h-[90px] font-mono text-xs"
                data-testid="promo-invite-emails"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Personal note <span className="text-muted-foreground">(optional, shown in the email)</span></Label>
              <Textarea
                value={inviteNote}
                onChange={e => setInviteNote(e.target.value)}
                placeholder="Hey! Would love your help getting the word out — easy money for every ticket you move."
                className="min-h-[60px] text-xs"
                data-testid="promo-invite-note"
              />
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={sendInvites} disabled={inviteSending} data-testid="promo-invite-send">
              {inviteSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Invites
            </Button>
            {sentInvites.length > 0 && (
              <div className="pt-2 border-t border-border max-h-40 overflow-y-auto space-y-1" data-testid="promo-invite-history">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Already invited ({sentInvites.length})</p>
                {sentInvites.map(inv => {
                  const status = inv.accepted ? { label: 'Joined ✓', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' }
                    : inv.clicked_at ? { label: 'Clicked Join', cls: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' }
                    : inv.opened_at ? { label: 'Opened', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' }
                    : { label: 'Sent', cls: 'bg-muted text-muted-foreground border-border' };
                  return (
                    <div key={inv.invite_id || inv.email} className="flex items-center justify-between gap-2 text-xs py-1" data-testid={`invite-row-${inv.email}`}>
                      <span className="truncate flex-1">{inv.email}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${status.cls}`} data-testid={`invite-status-${inv.email}`}>{status.label}</span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">{inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : ''}{(inv.send_count || 1) > 1 ? ` ·×${inv.send_count}` : ''}</span>
                      {!inv.accepted && (
                        <button
                          type="button"
                          onClick={() => nudgeInvite(inv.email)}
                          disabled={nudging === inv.email}
                          className="text-[10px] text-indigo-400 hover:text-indigo-300 font-semibold flex-shrink-0 disabled:opacity-50"
                          data-testid={`invite-nudge-${inv.email}`}
                        >
                          {nudging === inv.email ? '…' : 'Nudge'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-border bg-background/50 p-2.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3 w-3" /><span className="text-[10px] uppercase tracking-wide">{label}</span></div>
    <p className="text-lg font-bold">{value}</p>
  </div>
);
